/**
 * Core result types shared across validators, appliers, and scoring.
 *
 * - `ValidationResult` is what every validator returns. The `error` field is a
 *   stable `ERR_*` code; UI-facing strings are derived in the i18n layer (P11).
 * - `Result<T, E>` is the railway type used for non-validation operations
 *   (e.g. supply draws, replay). Per `docs/guides/coding-conventions/03-error-handling.md`.
 */

export interface ValidationResult {
  readonly valid: boolean;
  readonly error?: string;
  readonly details?: Readonly<Record<string, unknown>>;
}

export interface OkResult<T> {
  readonly ok: true;
  readonly value: T;
}

export interface ErrResult<E> {
  readonly ok: false;
  readonly error: E;
}

export type Result<T, E = string> = OkResult<T> | ErrResult<E>;

export const ok = <T>(value: T): OkResult<T> => ({ ok: true, value });
export const err = <E>(error: E): ErrResult<E> => ({ ok: false, error });

export const isOk = <T, E>(result: Result<T, E>): result is OkResult<T> =>
  result.ok;

export const isErr = <T, E>(result: Result<T, E>): result is ErrResult<E> =>
  !result.ok;
